import { EntityState, EntityStore, getEntityType } from "@datorama/akita";
import { CollectionService, WriteOptions } from "akita-ng-fire";
import { untilDestroyed } from "ngx-take-until-destroy";
import { OnDestroy, inject } from "@angular/core";
import { take, catchError, tap } from "rxjs/operators";
import { throwError, from, of } from "rxjs";
import * as Sentry from "@sentry/angular";
import { doc, getDoc, Firestore, QueryConstraint } from "@angular/fire/firestore";
import { environment } from "../../environments/environment";

export type QueryFn = QueryConstraint[];

export class FirebaseService<
  S extends EntityState<EntityType, string>,
  EntityType = getEntityType<S>
> extends CollectionService<S, EntityType> {
  constructor(store: EntityStore<S>) {
    super(store, undefined, inject(Firestore));
  }

  protected onDelete(id: string) {
    Sentry.addBreadcrumb({ message: `[${this.path}] Deleted  ${id}` });
    this.store.remove(id as any);
  }

  async add<D extends Partial<EntityType> | Partial<EntityType>[]>(
    documents: D,
    options?: WriteOptions
  ): Promise<D extends (infer _I)[] ? string[] : string> {
    const docs: EntityType[] = <EntityType[]>(Array.isArray(documents) ? documents : [documents]);
    docs.forEach(doc => {
      this.ensureId(doc);
      FirebaseService.removeNulls(doc);
    });

    if (environment.e2e) {
      return (Array.isArray(documents)
        ? (documents as Partial<EntityType>[]).map(d => d[this.idKey])
        : (documents as Partial<EntityType>)[this.idKey]) as any;
    }

    await super.add(docs, options);
    Sentry.addBreadcrumb({ message: `[${this.path}] Added documents` });
    return (Array.isArray(documents)
      ? (documents as Partial<EntityType>[]).map(d => d[this.idKey])
      : (documents as Partial<EntityType>)[this.idKey]) as any;
  }

  getActive(id: S["active"]) {
    if (environment.e2e) {
      this.store.setActive(id);
      this.store.setLoading(false);
      return of(null);
    }

    this.store.setActive(id);
    return from(getDoc(doc(this.collection, id as string)))
      .pipe(
        tap(docSnapshot => {
          if (!docSnapshot.exists()) {
            const err = `Could not find product: ${id}`;
            console.error(err);
            this.store.setLoading(false);
            this.store.setError(err);
            return;
          }

          Sentry.addBreadcrumb({ message: `[${this.path}] Loaded active ${id}` });
          const entity = { ...docSnapshot.data(), [this.idKey]: id } as unknown as getEntityType<S>;
          this.store.add(entity, { loading: false });
        }),
        catchError(err => {
          console.error(err);
          this.store.setError(err);
          this.store.setLoading(false);
          return throwError(err);
        })
      );
  }

  syncCollectionUntilDestroyed(component: OnDestroy, query?: QueryFn) {
    if (environment.e2e) {
      this.store.setLoading(false);
      return null;
    }

    return this.syncCollection(query, { reset: !!query })
      .pipe(untilDestroyed(component))
      .subscribe();
  }

  syncCollectionOnce(query?: QueryFn) {
    return this.syncCollection(query)
      .pipe(take(1))
      .subscribe();
  }

  private ensureId<T>(docData: T): void {
    if (!docData[this.idKey]) {
      // In modular SDK, we don't have createId on firestore object directly in the same way
      // We can use doc(collection(db, path)).id
      docData[this.idKey] = doc(this.collection).id;
    }
  }

  public static removeNulls<T>(doc: T): T {
    for (const attr in doc) {
      if (doc[attr] === null || doc[attr] === undefined) {
        delete doc[attr];
      }
    }
    return doc
  }

  getUser(): Sentry.User {
    try {
      const userString = window.localStorage && localStorage.getItem("user");
      if (userString) {
        const sentryUser: Sentry.User = JSON.parse(userString);
        return sentryUser;
      }
    } catch (e) {}

    return {};
  }
}
